/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { JSX, ReactNode } from "react";
import React, { useRef, useEffect, useState, useCallback } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { calculateZoomLevel, isHTMLElement } from "@lexical/utils";
import { $getRoot, COMMAND_PRIORITY_LOW, CommandListenerPriority, isDOMNode, LexicalEditor, LexicalNode } from "lexical";

import { createPortal } from "react-dom";

import { Point } from "../../shared/point";
import { Rectangle } from "../../shared/rect";

import { useSlidLexicalAddButtonStore } from "./utils/useSlidLexicalAddButtonStore";
import { LexicalMenu, MenuResolution, MenuOption, MenuRenderFn, useMenuAnchorRef } from "components/NewEditor/shared/LexicalMenu";
import { ReactComponent as AddBlockMenuIcon } from "../../images/icons/add-block-button.svg";

export type HoverMenuPluginProps<TOption extends MenuOption> = {
  onSelectOption: (option: TOption, textNodeContainingQuery: LexicalNode | null, closeMenu: () => void, matchingString: string) => void;
  options: Array<TOption>;
  onClose?: () => void;
  onOpen?: (resolution: MenuResolution) => void;
  menuRenderFn: MenuRenderFn<TOption>;
  anchorClassName?: string;
  commandPriority?: CommandListenerPriority;
  parent?: HTMLElement;
  isEditable: boolean;
  anchorElem: HTMLElement;
  scrollWrapper?: HTMLElement;
};

const SPACE = 0;

const Downward = 1;
const Upward = -1;
const Indeterminate = 0;

let prevIndex = Infinity;

function getCurrentIndex(keysLength: number): number {
  if (keysLength === 0) {
    return Infinity;
  }
  if (prevIndex >= 0 && prevIndex < keysLength) {
    return prevIndex;
  }

  return Math.floor(keysLength / 2);
}

function getTopLevelNodeKeys(editor: LexicalEditor): string[] {
  return editor.getEditorState().read(() => $getRoot().getChildrenKeys());
}

function getCollapsedMargins(elem: HTMLElement): {
  marginTop: number;
  marginBottom: number;
} {
  const getMargin = (element: Element | null, margin: "marginTop" | "marginBottom"): number => (element ? parseFloat(window.getComputedStyle(element)[margin]) : 0);

  const { marginTop, marginBottom } = window.getComputedStyle(elem);
  const prevElemSiblingMarginBottom = getMargin(elem.previousElementSibling, "marginBottom");
  const nextElemSiblingMarginTop = getMargin(elem.nextElementSibling, "marginTop");
  const collapsedTopMargin = Math.max(parseFloat(marginTop), prevElemSiblingMarginBottom);
  const collapsedBottomMargin = Math.max(parseFloat(marginBottom), nextElemSiblingMarginTop);

  return { marginBottom: collapsedBottomMargin, marginTop: collapsedTopMargin };
}

function getBlockElement(anchorElem: HTMLElement, editor: LexicalEditor, event: MouseEvent, useEdgeAsDefault = false): HTMLElement | null {
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const topLevelNodeKeys = getTopLevelNodeKeys(editor);

  let blockElem: HTMLElement | null = null;

  editor.getEditorState().read(() => {
    if (useEdgeAsDefault) {
      const [firstNode, lastNode] = [editor.getElementByKey(topLevelNodeKeys[0]), editor.getElementByKey(topLevelNodeKeys[topLevelNodeKeys.length - 1])];

      const [firstNodeRect, lastNodeRect] = [firstNode != null ? firstNode.getBoundingClientRect() : undefined, lastNode != null ? lastNode.getBoundingClientRect() : undefined];

      if (firstNodeRect && lastNodeRect) {
        const firstNodeZoom = calculateZoomLevel(firstNode);
        const lastNodeZoom = calculateZoomLevel(lastNode);
        if (event.y / firstNodeZoom < firstNodeRect.top) {
          blockElem = firstNode;
        } else if (event.y / lastNodeZoom > lastNodeRect.bottom) {
          blockElem = lastNode;
        }

        if (blockElem) {
          return;
        }
      }
    }

    let index = getCurrentIndex(topLevelNodeKeys.length);
    let direction = Indeterminate;

    while (index >= 0 && index < topLevelNodeKeys.length) {
      const key = topLevelNodeKeys[index];
      const elem = editor.getElementByKey(key);
      if (elem === null) {
        break;
      }
      // NOTE: on Slid desktop app, when zoom is 2, it's not working.
      // const zoom = calculateZoomLevel(elem);
      const zoom = 1;
      const point = new Point(event.x / zoom, event.y / zoom);
      const domRect = Rectangle.fromDOM(elem);
      const { marginTop, marginBottom } = getCollapsedMargins(elem);
      const rect = domRect.generateNewRect({
        bottom: domRect.bottom + marginBottom,
        left: anchorElementRect.left,
        right: anchorElementRect.right,
        top: domRect.top - marginTop,
      });

      const {
        result,
        reason: { isOnTopSide, isOnBottomSide },
      } = rect.contains(point);

      if (result) {
        blockElem = elem;
        prevIndex = index;
        break;
      }

      if (direction === Indeterminate) {
        if (isOnTopSide) {
          direction = Upward;
        } else if (isOnBottomSide) {
          direction = Downward;
        } else {
          // stop search block element
          direction = Infinity;
        }
      }

      index += direction;
    }
  });

  return blockElem;
}

function setMenuPosition(targetElem: HTMLElement | null, floatingElem: HTMLElement, anchorElem: HTMLElement) {
  if (!targetElem) {
    floatingElem.style.opacity = "0";
    floatingElem.style.transform = "translate(-10000px, -10000px)";
    return;
  }

  const targetRect = targetElem.getBoundingClientRect();
  const targetStyle = window.getComputedStyle(targetElem);
  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();

  // top left
  let targetCalculateHeight: number = parseInt(targetStyle.lineHeight, 10);
  if (isNaN(targetCalculateHeight)) {
    // middle
    targetCalculateHeight = targetRect.bottom - targetRect.top;
  }
  const top = targetRect.top + (targetCalculateHeight - floatingElemRect.height) / 2 - anchorElementRect.top;

  const left = SPACE;

  floatingElem.style.opacity = "1";
  floatingElem.style.transform = `translate(${left}px, ${top}px)`;
}

export function SlidLexicalAddButtonPlugin<TOption extends MenuOption>({
  options,
  onSelectOption,
  menuRenderFn,
  anchorClassName,
  anchorElem,
  commandPriority = COMMAND_PRIORITY_LOW,
  parent,
  isEditable,
  scrollWrapper,
}: HoverMenuPluginProps<TOption>): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [resolution, setResolution] = useState<MenuResolution | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const anchorElementRef = useMenuAnchorRef(resolution, setResolution, anchorClassName, parent);
  const { setIsMenuOpen } = useSlidLexicalAddButtonStore();
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollerElem = anchorElem.parentElement;
  const { targetBlockElem, setTargetBlockElem, isMenuOpen } = useSlidLexicalAddButtonStore();
  const isMenuOpenRef = useRef(isMenuOpen);
  const [buttonPosition, setButtonPosition] = useState<{ x: number; y: number } | null>(null);

  const closeMenu = useCallback(() => {
    setResolution(null);
  }, [resolution]);

  const handleButtonClick = useCallback(() => {
    setResolution({
      getRect: () => new DOMRect(buttonPosition?.x, buttonPosition?.y, 1, 1),
    });
  }, [buttonPosition]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        resolution !== null &&
        menuRef.current &&
        event.target &&
        isDOMNode(event.target) &&
        !menuRef.current.contains(event.target) &&
        (!buttonRef.current || !buttonRef.current.contains(event.target as Node))
      ) {
        closeMenu();
      }
    },
    [resolution, closeMenu]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    setIsMenuOpen(!!resolution);
  }, [resolution]);

  useEffect(() => {
    const editorScrollContainer = scrollWrapper;
    if (editorScrollContainer) {
      if (resolution) {
        editorScrollContainer.style.pointerEvents = "none";
      } else {
        editorScrollContainer.style.pointerEvents = "";
      }
    }

    return () => {
      if (editorScrollContainer) {
        editorScrollContainer.style.pointerEvents = "";
      }
    };
  }, [resolution, scrollWrapper]);

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  useEffect(() => {
    function onMouseMove(event: MouseEvent) {
      const target = event.target;
      if (!isHTMLElement(target)) {
        setTargetBlockElem(null);
        return;
      }

      if (isMenuOpenRef.current) {
        return;
      }

      const _draggableBlockElem = getBlockElement(anchorElem, editor, event);
      setTargetBlockElem(_draggableBlockElem);
      if (_draggableBlockElem) {
        const rect = _draggableBlockElem.getBoundingClientRect();
        setButtonPosition({ x: rect.left, y: rect.top + 20 });
      }
    }

    function onMouseLeave() {
      if (!isMenuOpenRef.current) {
        setTargetBlockElem(null);
        setButtonPosition(null);
      }
    }

    if (scrollerElem != null) {
      scrollerElem.addEventListener("mousemove", onMouseMove);
      scrollerElem.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      if (scrollerElem != null) {
        scrollerElem.removeEventListener("mousemove", onMouseMove);
        scrollerElem.removeEventListener("mouseleave", onMouseLeave);
      }
    };
  }, [scrollerElem, anchorElem, editor]);

  useEffect(() => {
    if (menuRef.current) {
      setMenuPosition(targetBlockElem, menuRef.current, anchorElem);
    }
  }, [anchorElem, targetBlockElem, menuRef]);

  return (
    <>
      {createPortal(
        <div>
          {isEditable && (
            <div ref={menuRef} className="icon add-block-menu" onClick={handleButtonClick}>
              <AddBlockMenuIcon />
              {anchorElementRef.current !== null && resolution !== null ? (
                <LexicalMenu
                  close={closeMenu}
                  resolution={resolution}
                  editor={editor}
                  anchorElementRef={anchorElementRef}
                  options={options}
                  menuRenderFn={menuRenderFn}
                  onSelectOption={onSelectOption}
                  commandPriority={commandPriority}
                />
              ) : null}
            </div>
          )}
        </div>,
        anchorElem
      )}
    </>
  );
}
